import { createSelector } from '@reduxjs/toolkit';
import find from 'lodash/find';
import qs from 'query-string';

import type { SIGNUP_SOURCES } from 'src/app/components/Pages/GetStarted/consts';
import type { STEP_KEY } from 'src/app/components/Pages/GetStarted/StepLayout/consts';
import { makeGetCollectionSelector } from 'src/app/popsicle/state/selectors';
import ConsumerWebPropTypes from 'src/app/propTypes';
import type { BffOrderHistoryState } from 'src/app/state/reducers';
import { getArchivedMedications } from 'src/app/state/slices/archivedMedications';
import type { RootState } from 'src/app/state/store';
import { COOKIE_IS_LOGGED_IN } from 'src/app/storage/cookieKeys';
import { getCookie } from 'src/app/storage/cookies';
import type {
  Cart,
  CartItemForCheckout,
  CheckoutDeliveryWindow,
  DeliveryMethod,
  Doctor,
  HomeItem,
  Medication,
  MedicationDetails,
  OTCCategory,
  PatientDetails,
  PickupStation,
} from 'src/app/types';
import { parseMarketingParams } from 'src/app/utils';
import { DELIVERY_MESSAGE, DeliveryMethodType, FACILITY_FALLBACK } from 'src/consts';
import type { DeliveryMethodLabels } from 'src/consts';

export * from './modal';
export * from './promoCode';
export * from './userPreferences';

/**
 * Basic selectors- not memoized
 */

export const getClient = (state: RootState) => state.client;

const getSession = (state: RootState) => state?.session;

export const getCurrentUserId = createSelector(getSession, sessionState => sessionState?.currentUserId);
export const getCurrentAccountId = createSelector(getSession, sessionState => sessionState?.currentAccountId ?? '');

export const getAccount = (state: RootState) => state.account;
export const getAccountIsFetching = createSelector(getAccount, accountState => accountState?.isFetching);

export const isLoggedIn = (): boolean => {
  // Super hacky workaround...
  // I tried to remove isLoggedIn flag in favor of auth context provider isLoggedIn flag, but couldn't because
  // how integrated this flag is in the onboarding flow components. We must refactor the onboarding flow
  // to get rid of this.
  return Boolean(getCookie(COOKIE_IS_LOGGED_IN));
};

export const getIsAccountFetched = createSelector(getAccount, state => state.data != null);
export const getCurrentUser = createSelector(getAccount, state => state.data?.user);
export const getAccountReferral = createSelector(getAccount, state => state?.data?.referral);
export const getAccountEmail = createSelector(getAccount, state => state?.data?.email);
export const getAccountSignupSource = createSelector(
  getAccount,
  state => (state?.data?.signupSource ?? state?.data?.attributes?.signup_source) as SIGNUP_SOURCES | undefined
);
export const getAccountDefaultCardId = createSelector(getAccount, account => account?.data?.stripeDefaultCardId);
export const getAccountZipCode = createSelector(getAccount, state => state.data?.zipCode);
export const getAccountIDVerificationStatus = createSelector(getAccount, state => state.data?.idVerificationStatus);

// As of Oct 2022, There's 1, 2, and 3
export const getAccountAgreedToTermsVersion = createSelector(getAccount, account => {
  const versionNumber = account?.data?.attributes?.agreed_to_terms_version ?? account?.data?.agreedToTermsVersion;
  return versionNumber ? `${versionNumber}` : undefined;
});

export const getUserIsLoginSet = createSelector(getCurrentUser, state => state?.isLoginSet);

export const getCurrentOrderId = (state: RootState): string => state?.checkout?.orderId;
export const getCurrentCartId = (state: RootState): string => state?.checkout?.cartId;
export const getSelectedDeliveryMethodLabelFromCheckout = (state: RootState): DeliveryMethodLabels =>
  state?.checkout?.selectedDeliveryMethodLabel;

export const getBFFCart = createSelector(
  (state: RootState) => state?.bffCart,
  state => state?.data
);

export const getBffCartPrescriptionCartItems = createSelector(getBFFCart, state =>
  state?.prescriptionCartItems?.flatMap(i => i.items)
);

export const getCurrentCartUUID = (state: RootState): string => getBFFCart(state)?.uuid;
export const getBFFCartDeliveryAddress = (state: RootState) => getBFFCart(state)?.deliveryAddress;
export const getBFFCartDeliveryMethod = (state: RootState) => getBFFCart(state)?.deliveryMethod;
const getBFFCartDeliveryMethodKey = createSelector(
  getBFFCartDeliveryMethod,
  bffCartDeliveryMethod => bffCartDeliveryMethod?.key
);
export const getHasSelectedPickupStationAsDeliveryMethod = createSelector(
  getBFFCartDeliveryMethodKey,
  bffCartDeliveryMethodKey => bffCartDeliveryMethodKey === DeliveryMethodType.pickupStation
);
export const getHasSelectedCourierAsDeliveryMethod = createSelector(
  getBFFCartDeliveryMethodKey,
  bffCartDeliveryMethodKey => bffCartDeliveryMethodKey === DeliveryMethodType.courier
);
export const getBFFCartDeliveryWindow = createSelector(
  getBFFCartDeliveryMethod,
  deliveryMethod => deliveryMethod?.deliveryWindow
);
export const getBFFCartDeliveryWindowId = createSelector(getBFFCartDeliveryWindow, window => window?.id);
export const getBFFCartPaymentMethod = (state: RootState) => getBFFCart(state)?.paymentMethod;
export const getBFFCartDeliveryMethods = createSelector(
  (state: RootState) => state?.bffCartDeliveryMethods,
  state => state?.data
);

export const getBFFCartTax = createSelector(getBFFCart, (bffCartState: Cart) => bffCartState?.tax);
export const getBFFCartTip = createSelector(getBFFCart, (bffCartState: Cart) => bffCartState?.tip);
export const getBFFCartSubtotal = createSelector(getBFFCart, (bffCartState: Cart) => bffCartState?.subtotal);
export const getBFFCartTotal = createSelector(getBFFCart, (bffCartState: Cart) => bffCartState?.total);

export const getIsBFFCartDeliveryMethodsFetched = createSelector(
  getBFFCartDeliveryMethods,
  deliveryMethods => deliveryMethods != null
);

export const getBFFCartDeliveryMethodsIsFetching = createSelector(
  (state: RootState) => state?.bffCartDeliveryMethods,
  state => state?.isFetching
);

export const getBFFCartDeliveryMethodNames = createSelector(
  (state: RootState) => getBFFCartDeliveryMethods(state),
  deliveryMethods => deliveryMethods?.map(({ name }) => name)
);

export const isDeliveryMethodsESIMailEligible = (deliveryMethods: DeliveryMethod[]): boolean =>
  deliveryMethods?.some(({ key }) => key === DeliveryMethodType.mail);

export const getIsUserESIMailEligible = createSelector(
  (state: RootState) => getBFFCartDeliveryMethods(state),
  state => isDeliveryMethodsESIMailEligible(state)
);
export const getIsBffCartFetching = (state: RootState) => state?.bffCart?.isFetching;
export const getOrderSubtotal = (state: RootState) => state?.bffCart?.data?.subtotal;

export const getCartItems = (state: RootState): CartItemForCheckout[] => state?.checkout?.cart ?? [];

export const getCartItemQuantity = createSelector(getCartItems, items => {
  const totalQuantity = [...items].reduce((tmpTotalQuantity, cartItem) => {
    if (cartItem.otcItemId) {
      tmpTotalQuantity += cartItem.quantity ?? 0;
    } else {
      tmpTotalQuantity += 1;
    }
    return tmpTotalQuantity;
  }, 0);

  return totalQuantity;
});

export const getCheckoutPatientMedicationIds = (state: RootState): number[] =>
  state?.checkout?.cart?.map(item => item.patientMedicationId);

export const getPathParams: (op: any, p: string) => string = (ownProps, paramName) =>
  ownProps?.match?.params?.[paramName];

export const createGetDeliveryAddressesSelector = () => {
  return makeGetCollectionSelector(ConsumerWebPropTypes.deliveryAddress, {
    filter: deliveryAddress => deliveryAddress.attributes.is_active && deliveryAddress.id !== 'None',
  });
};

const getDeliveryAddressId = (state: RootState) => {
  const getDeliveryAddresses = createGetDeliveryAddressesSelector();
  // Delivery Address
  // Check if one is selected || Fallback to default address only if it is active.
  const deliveryAddresses = getDeliveryAddresses(state);
  const defaultAddress = deliveryAddresses.filter(e => e.attributes.is_default)[0];
  const deliveryAddressCheckoutId = state.checkout?.DeliveryAddress?.id ?? null;

  // Check the deliveryAddresses since it gets updated on every refresh and already filters for is_active
  // Do not need to refresh the store or call an api
  const deliveryAddressCheckoutIsActive = find(deliveryAddresses, { id: deliveryAddressCheckoutId });
  const deliveryAddressId = deliveryAddressCheckoutIsActive?.id || defaultAddress?.id;
  return deliveryAddressId;
};

export const getTierConsumerMessage = (state: RootState) => {
  const deliveryAddressId = getDeliveryAddressId(state);
  const deliveryAddress = state?.resources?.DeliveryAddress?.[deliveryAddressId];
  const tierId = deliveryAddress?.attributes?.delivery_zone_category;
  if (tierId in DELIVERY_MESSAGE) {
    return DELIVERY_MESSAGE[tierId];
  }
  return null;
};

const medicationManagementState = (state: RootState) => state.medicationManagement;

export const getMedicationManagementData = (state: RootState) => state.medicationManagement.data;

export const getUnarchivedMedications = createSelector(
  medicationManagementState,
  state => state?.data?.active as HomeItem[] | undefined
);

export const getActiveMedicationItems = createSelector(getUnarchivedMedications, activeMeds =>
  activeMeds?.flatMap(med => med.items)
);

export const getPendingTransfers = createSelector(medicationManagementState, state => state?.pendingTransfers || []);

export const getHasFetchedHomeMedications = createSelector(medicationManagementState, state => !!state?.hasFetched);

export const getHasPrimaryPatientEverTransferred = createSelector(
  medicationManagementState,
  getHasFetchedHomeMedications,
  state => !!state?.hasPrimaryEverSubmittedTransfer
);

export const getOptimisticPendingTransfers = createSelector(
  medicationManagementState,
  state => state?.optimisticPendingTransfers || []
);

export const getNumPendingTransfers = createSelector(
  getPendingTransfers,
  getOptimisticPendingTransfers,
  (pendingTransfers, optimisticPendingTransfers) => {
    return pendingTransfers.length || optimisticPendingTransfers.length || 0;
  }
);

const getPotentialTransfers = createSelector(
  medicationManagementState,
  state => (state?.data?.potential ?? []) as { doctor: Doctor; items: Medication[] }[]
);

export const getPotentialTransferItems = createSelector(getPotentialTransfers, transfers => {
  const items = transfers.flatMap(({ doctor, items }) => items.map(item => ({ doctor, ...item })));
  return items;
});

export const getIsFetchingMedications = createSelector(medicationManagementState, state => state?.isFetching);
export const getHasMedications = createSelector(
  getUnarchivedMedications,
  getArchivedMedications,
  (activeMeds, archivedMeds) => {
    return (
      activeMeds?.some(record => record?.items?.length > 0) || archivedMeds?.some(record => record?.items?.length > 0)
    );
  }
);

export const getTransferEscriptCounts = (state: RootState) => state.transfers.escriptCounts;

export const getCheckoutDeliveryWindows = createSelector(
  (state: RootState) => state?.checkoutDeliveryWindows?.data,
  items => items
);

export const getCheckoutDeliveryWindowsIsFetching = createSelector(
  (state: RootState) => state?.checkoutDeliveryWindows?.isFetching,
  isFetching => isFetching
);

export const getCheckoutDeliveryWindowsFetchError = createSelector(
  (state: RootState) => state?.checkoutDeliveryWindows?.error,
  error => error
);

export const getCheckoutDeliveryWindowTimes = createSelector(getCheckoutDeliveryWindows, days =>
  days?.flatMap((day: CheckoutDeliveryWindow) => day?.deliveryWindows)
);

export const getIsUserPartOfCapsuleAtWork = createSelector(getAccountReferral, referralString => {
  return referralString === 'morganstanley';
});

const getBffPatient = (state: RootState) => state.patient;
export const getPatients = createSelector(getBffPatient, patientState => patientState?.data);
export const getIsFetchingBffPatient = createSelector(getBffPatient, patientState => patientState.isFetching);
export const getPrimaryPatient = createSelector(getPatients, (patients: PatientDetails[]) => {
  return patients?.find(patient => patient?.isPrimary) ?? null;
});
export const getPrimaryPatientId = createSelector(getPrimaryPatient, primaryPatient => primaryPatient?.id ?? null);

export const getBffDeliveryAddress = (state: RootState) => state.deliveryAddress;
export const getDeliveryAddresses = createSelector(getBffDeliveryAddress, state => state?.data);
export const getIsFetchingDeliveryAddresses = createSelector(getBffDeliveryAddress, state => state?.isFetching);

const getBffPaymentCards = (state: RootState) => state.paymentCards;
export const getPaymentCards = createSelector(getBffPaymentCards, paymentCards => paymentCards?.data);
export const getPaymentCard = (paymentCardId: string) =>
  createSelector(getPaymentCards, paymentCards => paymentCards.find(card => card.id === paymentCardId));

const getLegacyInsurances = (state: RootState) => state.insurances;
export const getHasFetchedInsurance = createSelector(getLegacyInsurances, insurances => insurances?.hasFetched);
const getInsuranceUpload = createSelector(getLegacyInsurances, insurances => insurances?.uploadInsurance);
export const getIsInsuranceUploadSuccessful = createSelector(getInsuranceUpload, state => state?.isUploaded);

export type ReduxOTCCategories = { isFetching: boolean; data: OTCCategory[] | null; error?: any };
export const getOTCCategories = (state: RootState) => state.bffOTCCategories as ReduxOTCCategories;

const bffOrderHistoryState = (state: RootState) => state.bffOrderHistory;

/**
 * Order History Selectors
 */

export const getIsFetchingBFFOrders = createSelector(
  bffOrderHistoryState,
  (state: BffOrderHistoryState) => state?.isFetching
);

export const getHasFetchedOrders = createSelector(bffOrderHistoryState, state => state.hasFetched);

export const getBFFOrdersActive = createSelector(
  bffOrderHistoryState,
  (state: BffOrderHistoryState) => state?.data?.active
);

export const getBFFOrdersCompleted = createSelector(
  bffOrderHistoryState,
  (state: BffOrderHistoryState) => state?.data?.past
);

export const getAllBFFOrders = createSelector(bffOrderHistoryState, (state: BffOrderHistoryState) => {
  if (state?.data?.active) {
    //assuming active also means past exists and is empty
    return [...state?.data?.active, ...state?.data?.past];
  }
  return [];
});

export const getBFFOrder = (state: RootState, orderId: string) => state?.bffOrder?.data?.[orderId];

// TODO: Remove?
export const getIsAllergyProfileComplete = createSelector(getPrimaryPatient, (patient: PatientDetails | null) => {
  return Boolean(
    patient?.allergies !== null || patient?.otherMedications !== null || patient?.medicalConditions !== null
  );
});

export const getOrderNeedsCreditCart = (state: RootState) => {
  const cart = getBFFCart(state);
  if (!cart) return false;

  return cart?.total > 0;
};

export const getCurrentPathname = (state: RootState) => state?.router?.location?.pathname;

/** @deprecated - Use `getSearch` from `connected-react-router` */
export const getCurrentPathParams = (state: RootState) => state?.router?.location?.search;
export const getCurrentMarketingPathParams = createSelector(getCurrentPathParams, search => {
  const marketingParams = parseMarketingParams(search);
  return qs.stringify(marketingParams);
});

export const getOnboardingSteps = (state: RootState) => state.onboardingSteps;

export const getOnboardingCurrentIndex = (state: RootState) =>
  getOnboardingSteps(state)?.findIndex(
    step =>
      step.stepName === state.onboardingCurrentStep || step.additionalLayouts?.includes(state.onboardingCurrentStep)
  );

export const getOnboardingCurrentStep = (state: RootState) => state.onboardingCurrentStep;

export const getOnboardingStepsLength = (state: RootState) => getOnboardingSteps(state)?.length;

export const getOnboardingIndexByStepKey = (state, keyName: STEP_KEY) =>
  getOnboardingSteps(state)?.findIndex(step => step.key === keyName);

export const getOnboardingProgressFillPercent = (state: RootState) => {
  const stepIdx = getOnboardingCurrentIndex(state);
  return getOnboardingSteps(state)[stepIdx]?.progressBarFillPercent;
};

const getRouterLocation = (state: RootState) => state?.routerLocation;

export const getPreviousPathName = createSelector(getRouterLocation, state => state?.previous?.pathname);

const getMedicationDetailsState = (state: RootState) => state?.medicationDetails;

const getCurrentPrescriptionId = createSelector(
  getMedicationDetailsState,
  medicationDetailsState => medicationDetailsState?.currentPrescriptionId
);
const getCurrentMedicationDetail = createSelector(
  getMedicationDetailsState,
  getCurrentPrescriptionId,
  (medicationDetailsState, prescriptionId) => medicationDetailsState.data[prescriptionId] as MedicationDetails
);
export const getCurrentMedicationDetailErrorResponse = createSelector(
  getMedicationDetailsState,
  medicationDetailsState => medicationDetailsState?.error?.response?.data
);
export const getCurrentMedicationDetailPrescription = createSelector(
  getCurrentMedicationDetail,
  medicationDetailsState => medicationDetailsState?.prescription
);
export const getCurrentMedicationDetailProvider = createSelector(
  getCurrentMedicationDetail,
  medicationDetailsState => medicationDetailsState?.provider
);
export const getCurrentMedicationDetailWarningLabels = createSelector(
  getCurrentMedicationDetail,
  medicationDetailsState => medicationDetailsState?.warningLabels
);
const getCurrentMedicationDetailNDC = createSelector(
  getCurrentMedicationDetail,
  medicationDetailsState => medicationDetailsState?.ndc
);
export const getCurrentMedicationDetailPatientEducation = createSelector(
  getCurrentMedicationDetail,
  medicationDetailsState => medicationDetailsState?.patientEducation
);
export const getHasMedicationDetailsLoaded = createSelector(
  state => state,
  getMedicationDetailsState,
  (state, medicationDetailsState) => !!getCurrentMedicationDetail(state) || !!medicationDetailsState?.error
);
export const getCurrentMedicationDetailPrescriptionLifecycle = createSelector(
  getCurrentMedicationDetail,
  medicationDetailsState => medicationDetailsState?.prescriptionLifecycle
);

export const getPrescriptionIdInPathParam = (_state: RootState, ownProps: OwnPropsType) =>
  getPathParams(ownProps, 'prescriptionId');

const getCheckoutPickupStationsState = (state: RootState) => state.checkoutPickupStations;
const getCheckoutPickupStationsData = createSelector(
  getCheckoutPickupStationsState,
  checkoutPickupStationsState => checkoutPickupStationsState.data
);
export const getCheckoutPickupStations = createSelector(
  getCheckoutPickupStationsData,
  checkoutPickupStationsData => checkoutPickupStationsData?.pickupStations as PickupStation[]
);
export const getCheckoutPickupStationsIsFetching = createSelector(
  getCheckoutPickupStationsState,
  checkoutPickupStationsState => checkoutPickupStationsState?.isFetching
);

export const getIsEligibleForPickups = createSelector(
  getCheckoutPickupStations,
  checkoutPickupStations => checkoutPickupStations?.length > 0
);

const getMedicationGuidesState = (state: RootState) => state?.medicationGuides;
export const getNDCPathParam = (_state, ownProps) => ownProps.match.params.ndc;

const getCurrentMedicationGuide = createSelector(
  getCurrentMedicationDetailNDC,
  getNDCPathParam,
  getMedicationGuidesState,
  (medDetailNDC, ndcPathParam, medicationGuidesState) => medicationGuidesState.data[medDetailNDC || ndcPathParam]
);

export const getCurrentMedicationGuideHtml = createSelector(
  getCurrentMedicationGuide,
  medicationGuidesState => medicationGuidesState?.html
);

export const getHasMedicationGuidesLoaded = createSelector(
  (state, ownProps) => ({ state, ownProps }),
  getMedicationGuidesState,
  ({ state, ownProps }, medicationGuidesState) =>
    !!getCurrentMedicationGuide(state, ownProps) || !!medicationGuidesState?.error
);

export const shouldShowFaqModal = (state: RootState) => state.displayModal.faq.showModal;

export const getIsOnboardingDoctorTransfer = (state: RootState) => state.onboardingForm.isDoctorTransfer;
export const getOnboardingFlow = (state: RootState) => state.onboardingForm.flow;
export const getOnboardingForm = (state: RootState) => state.onboardingForm;
/**
 * Get facility from Redux State. If no facility was set, use NY as the Facility Fallback
 * @param state
 * @returns facilityState
 */
export const getFacility = (state: RootState) => {
  const facilityFromState = state.facility;
  if (facilityFromState.isFetching === false && !facilityFromState.id) return FACILITY_FALLBACK;
  return facilityFromState;
};

export const getMedicationLoadingState = (state: RootState) => state.medicationLoadingState;
